import { Heading } from '@insify/components';
import { Button } from '@insify/design-system/components';
import { IconicCheck2, IconicClose } from '@insify/design-system/iconic-icons';
import { Globe } from '@insify/design-system/icons';
import { headingSm } from '@insify/design-system/typography';
import { useBreakpoint } from '@insify/react-hooks';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './LanguageSelect.module.css';

type SupportedLanguage = 'en' | 'de' | 'nl' | 'fr';

interface LanguageOption {
  code: SupportedLanguage;
  name: string;
  flag: string;
}

interface LanguageSelectProps {
  title?: string;
  menuPosition?: 'auto' | 'center' | 'bottom';
  className?: string;
}

const LanguageSelect: React.FC<LanguageSelectProps> = ({ title, menuPosition = 'auto', className }) => {
  const { i18n, t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const isMobile = !useBreakpoint('(min-width: 960px)');
  const selectorRef = useRef<HTMLDivElement>(null);

  const languages: LanguageOption[] = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
  ];

  const localizedTitle = title || t('common.selectLanguage', 'Select your language');

  // Handle clicks outside to close the menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Toggle menu
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Handle language change
  const changeLanguage = (code: SupportedLanguage) => {
    i18n.changeLanguage(code);
    setIsMenuOpen(false);
  };

  // Determine display position for menu
  const displayPosition = menuPosition === 'auto' ? (isMobile ? 'bottom' : 'center') : menuPosition;

  return (
    <div className={clsx(styles.container, className)} ref={selectorRef}>
      <div
        className={styles.toggleButton}
        onClick={toggleMenu}
        aria-label={t('common.toggleLanguageMenu', 'Toggle language menu')}
        title={t('common.toggleLanguageMenu', 'Toggle language menu')}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            toggleMenu();
          }
        }}
      >
        <Globe className={styles.globe} />
      </div>

      {/* Selector menu */}
      {isMenuOpen && (
        <div className={clsx(styles.menu, displayPosition === 'center' ? styles.menuCenter : styles.menuBottom)}>
          <div
            className={styles.overlay}
            onClick={() => setIsMenuOpen(false)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                setIsMenuOpen(false);
              }
            }}
          ></div>

          <div className={displayPosition === 'center' ? styles.menuCenterContent : styles.menuBottomContent}>
            <div className={styles.menuHeader}>
              <Heading className={clsx(headingSm, styles.menuTitle)}>{localizedTitle}</Heading>
              <Button
                kind='neutral'
                type='button'
                leftIcon={<IconicClose />}
                trackingId='close-language-menu'
                className={styles.closeButton}
                onClick={() => setIsMenuOpen(false)}
                aria-label={t('common.close', 'Close')}
              />
            </div>

            <ul className={styles.languageList}>
              {languages.map((lang) => (
                <li
                  key={lang.code}
                  className={`${styles.languageItem} ${i18n.language === lang.code ? styles.selectedLanguage : ''}`}
                  onClick={() => changeLanguage(lang.code)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      changeLanguage(lang.code);
                    }
                  }}
                >
                  <span className={styles.flag}>{lang.flag}</span>
                  <span className={styles.languageName}>{lang.name}</span>
                  {i18n.language === lang.code && <IconicCheck2 />}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export { LanguageSelect };
